import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { ref, onValue } from "firebase/database";
import { Clock, Download, FileText, CheckCircle, AlertCircle } from "lucide-react";

const SubheDashboard1 = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [userKey, setUserKey] = useState(null);
  const [lotteryWorks, setLotteryWorks] = useState([]);
  const [userDataStatus, setUserDataStatus] = useState({});

  const handleDownloadChalan = (work, type) => {
    navigate("/subhe-dashboard/challan-download", {
      state: { workData: work, chalanType: type },
    });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("currentUser"));
    if (user && user._key) {
      setUserKey(user._key);
      const node = user._node || "ekamwatapzp/EngineerRegistrations";

      const userRef = ref(db, `${node}/${user._key}`);
      const unsubUser = onValue(userRef, (snapshot) => {
        setUserDataStatus(snapshot.exists() ? snapshot.val() : {});
      });

      const docsRef = ref(
        db,
        `ekamwatapzp/EngineerRegistrations/${user._key}/documents`
      );
      const unsubDocs = onValue(docsRef, (snapshot) => {
        setUploadedFiles(snapshot.exists() ? snapshot.val() : {});
      });

      const lotteryRef = ref(db, `${node}/${user._key}/GeneratedLottery`);
      const unsubLottery = onValue(lotteryRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const flattened = Object.entries(data).map(([dateStr, work]) => ({
            ...work,
            id: work.id || work.workCode || dateStr,
            _key: work.id || work.workCode || dateStr,
            meetingNumber: work.meetingNumber || dateStr,
            allocatedAt: work.generatedAt || work.allocatedAt || dateStr,
            schemeName: work.schemeName || "योजना उपलब्ध नाही",
          }));
          setLotteryWorks(flattened);
        } else {
          setLotteryWorks([]);
        }
        setLoading(false);
      });

      return () => {
        unsubDocs();
        unsubLottery();
        unsubUser();
      };
    } else {
      setLoading(false);
    }
  }, []);

  const getRoleStatus = (roleName) => {
    if (!userDataStatus) return "Pending";
    const formattedRole = roleName.toLowerCase();

    if (formattedRole === "incharge") {
      const s = userDataStatus.deputyEngStatus || userDataStatus.assistantStatus;
      if (s && ["approved", "accepted"].includes(s.toLowerCase())) return "Approved";
    }

    if (formattedRole === "accountant") {
      const s =
        userDataStatus.payments?.["Registration invoice"]?.accountantStatus ||
        userDataStatus.payments?.["Registration status invoice"]?.accountantStatus;
      if (s && ["approved", "accepted"].includes(s.toLowerCase())) return "Approved";
    }

    const roleField = `approvedBy${roleName.charAt(0).toUpperCase() + roleName.slice(1)}`;
    const docIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    if (uploadedFiles && Object.keys(uploadedFiles).length > 0) {
      const isAllApproved = docIds.every((id) => {
        const doc = uploadedFiles[id];
        return doc && doc[roleField] === true;
      });
      if (isAllApproved) return "Approved";
    }

    const directStatus = userDataStatus[formattedRole + "Status"];
    if (directStatus && ["approved", "accepted"].includes(directStatus.toLowerCase()))
      return "Approved";

    return "Pending";
  };

  const steps = [
    { label: "Incharge", status: getRoleStatus("Incharge") },
    { label: "Accountant", status: getRoleStatus("Accountant") },
    { label: "Engineer", status: getRoleStatus("Engineer") },
    { label: "Admin", status: getRoleStatus("Admin") },
    { label: "CEO", status: getRoleStatus("CEO") },
  ];

  /* ─────────────────────────────────────────── */
  return (
    <div className="pb-10 px-3 sm:px-5 max-w-7xl mx-auto">

      {/* ══ HEADER ══ */}
      <div className="flex flex-col items-center gap-3 mb-6 sm:flex-row sm:justify-between">
        <h1 className="text-xl font-bold text-[#22165f] sm:text-2xl">
          डॅशबोर्ड (Dashboard)
        </h1>

        <button
          onClick={() => {
            if (lotteryWorks.length > 0) {
              navigate("/subhe-dashboard/selection-letter", {
                state: { workData: lotteryWorks[0] },
              });
            } else {
              alert("कोणतेही काम वाटप झालेले नाही.");
            }
          }}
          className="w-full sm:w-auto bg-[#22165f] text-white px-8 py-2.5 rounded-full font-black text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all animate-pulse border-2 border-white ring-4 ring-[#22165f]/25"
        >
          निवड पत्र
        </button>

        <p className="text-xs text-gray-500 sm:text-sm sm:text-right">
          सांगली जिल्हा परिषद / Subhe Dashboard
        </p>
      </div>

      {/* ══ MAIN CARD ══ */}
      <div className="bg-white rounded-2xl shadow-lg p-3 sm:p-6 space-y-6">

        {/* ── STATUS TRACKER ── */}
        <div className="border border-gray-300 rounded-xl p-4 sm:p-6">
          <h2 className="text-center font-bold mb-5 text-base sm:text-lg text-gray-700 tracking-wide uppercase">
            Status Details
          </h2>

          {/* MOBILE: vertical list */}
          <div className="flex flex-col gap-2 sm:hidden">
            {steps.map((step, i) => {
              const approved = step.status === "Approved";
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 ${
                    approved
                      ? "border-green-200 bg-green-50"
                      : "border-yellow-200 bg-yellow-50"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      approved ? "bg-green-500" : "bg-yellow-400"
                    }`}
                  >
                    {approved ? (
                      <CheckCircle size={20} className="text-white" />
                    ) : (
                      <Clock size={18} className="text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-800">{step.label}</p>
                  </div>
                  <span
                    className={`text-xs font-extrabold uppercase px-2 py-1 rounded-full ${
                      approved
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {approved ? "Approved" : "Pending"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* DESKTOP: horizontal stepper */}
          <div className="relative hidden sm:flex justify-between items-start">
            <div className="absolute left-0 right-0 top-5 h-2 bg-gray-200 rounded" />
            {steps.map((step, i) => {
              const approved = step.status === "Approved";
              return (
                <div key={i} className="relative z-10 flex flex-col items-center flex-1 px-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow ${
                      approved ? "bg-green-500" : "bg-yellow-400"
                    }`}
                  >
                    {approved ? (
                      <span className="text-lg">✓</span>
                    ) : (
                      <Clock size={20} className="text-white" />
                    )}
                  </div>
                  <p className="mt-2 text-[13px] font-black text-gray-800 text-center">
                    {step.label}
                  </p>
                  <span
                    className={`text-[11px] font-black uppercase ${
                      approved ? "text-green-600" : "text-yellow-600"
                    }`}
                  >
                    {approved ? "Approved" : "Pending"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── LOTTERY WORKS ── */}
        {/* MOBILE: cards */}
        <div className="block md:hidden space-y-3">
          <h3 className="font-bold text-sm text-gray-700 uppercase tracking-wide border-b pb-2">
            वाटप झालेली कामे
          </h3>
          {lotteryWorks.length > 0 ? (
            lotteryWorks.map((work, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl p-4 shadow-sm bg-gray-50 space-y-3"
              >
                {/* Row 1 */}
                <div className="flex justify-between items-start gap-2">
                  <span className="bg-[#22165f] text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                    #{index + 1}
                  </span>
                  <p className="text-sm font-bold text-gray-800 text-right flex-1 leading-snug">
                    {work.workName}
                  </p>
                </div>

                {/* Grid of details */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide">योजना</p>
                    <p className="font-bold text-[#22165f] leading-tight">{work.schemeName}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-semibold uppercase tracking-wide">रक्कम</p>
                    <p className="font-bold text-gray-800">{work.tenderAmount} Lakh</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400 font-semibold uppercase tracking-wide">लॉटरी दिनांक</p>
                    <p className="font-semibold text-gray-700">
                      {work.allocatedAt
                        ? new Date(work.allocatedAt).toLocaleDateString("mr-IN")
                        : "-"}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-1 border-t border-gray-200">
                  <button
                    onClick={() => handleDownloadChalan(work, "security_deposit")}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold py-2 rounded-lg border border-blue-200 active:scale-95 transition-all"
                  >
                    <Download size={14} /> सुरक्षा चलन
                  </button>
                  <button
                    onClick={() => handleDownloadChalan(work, "tender_fee")}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold py-2 rounded-lg border border-blue-200 active:scale-95 transition-all"
                  >
                    <Download size={14} /> फॉर्म फी
                  </button>
                  <button
                    onClick={() =>
                      navigate("/subhe-dashboard/work-order", {
                        state: { workData: work },
                      })
                    }
                    className="flex-1 flex items-center justify-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-[#22165f] text-xs font-bold py-2 rounded-lg border border-indigo-200 active:scale-95 transition-all"
                  >
                    <FileText size={14} /> आदेश
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <AlertCircle size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">काम उपलब्ध नाही</p>
              <p className="text-xs">(No Works Available)</p>
            </div>
          )}
        </div>

        {/* DESKTOP: full table */}
        <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-300">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="bg-[#22165f] text-white">
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">Sr.No.</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">कामाचे नाव</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">योजनेचे नांव</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">कामाची रक्कम</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">लॉटरी दिनांक</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">सुरक्षा अनामत चलन</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap bg-indigo-900">निविदा फॉर्म फी</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap bg-indigo-900">कार्यारंभ आदेश</th>
              </tr>
            </thead>
            <tbody>
              {lotteryWorks.length > 0 ? (
                lotteryWorks.map((work, index) => (
                  <tr
                    key={index}
                    className={`transition-colors ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-blue-50`}
                  >
                    <td className="border border-gray-200 p-2 text-center font-bold text-gray-600">{index + 1}</td>
                    <td className="border border-gray-200 p-2">{work.workName}</td>
                    <td className="border border-gray-200 p-2 font-bold text-[#22165f]">{work.schemeName}</td>
                    <td className="border border-gray-200 p-2 text-center whitespace-nowrap">{work.tenderAmount} Lakh</td>
                    <td className="border border-gray-200 p-2 text-center whitespace-nowrap">
                      {work.allocatedAt ? new Date(work.allocatedAt).toLocaleDateString() : "-"}
                    </td>
                    <td className="border border-gray-200 p-2 text-center">
                      <button
                        onClick={() => handleDownloadChalan(work, "security_deposit")}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors"
                        title="चलन डाउनलोड"
                      >
                        <Download size={18} />
                      </button>
                    </td>
                    <td className="border border-gray-200 p-2 text-center">
                      <button
                        onClick={() => handleDownloadChalan(work, "tender_fee")}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors"
                        title="वर्क चलन"
                      >
                        <Download size={18} />
                      </button>
                    </td>
                    <td className="border border-gray-200 p-2 text-center">
                      <button
                        onClick={() =>
                          navigate("/subhe-dashboard/work-order", {
                            state: { workData: work },
                          })
                        }
                        className="text-[#22165f] hover:text-blue-800 hover:bg-indigo-50 p-1.5 rounded transition-colors"
                        title="कार्यारंभ आदेश डाउनलोड करा"
                      >
                        <FileText size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="border border-gray-200 text-center p-6 text-gray-400">
                    काम उपलब्ध नाही (No Works Available)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── INFO BOX ── */}
        <div className="bg-emerald-500 text-white p-4 sm:p-5 rounded-xl text-sm sm:text-base leading-relaxed font-medium text-center">
          रेजिस्ट्रेशन प्रोसेस आणि लॉग इन झाल्यानंतर सर्वप्रथम ज्यांचे
          रेजिस्ट्रेशन प्रमाणपत्र/लायसन्स आहे, त्यांनी मागील ५ वर्षामध्ये
          केलेल्या कामाचा तपशील भरून घ्यावा. पासवर्ड अपडेट झाल्यावर कामाचा
          तपशील भरावा. याच्या पडताळणी नंतर तुम्हाला पुढील कामामध्ये ऑनलाइन
          भाग घेता येईल.
        </div>

        {/* ── FILTER ROW ── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
          <div className="flex-1">
            <label className="block font-bold mb-1.5 text-sm text-gray-700">
              वर्ष निवडा <span className="text-red-500">*</span>
            </label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white">
              <option>वर्ष निवडा</option>
              <option>2023-24</option>
              <option>2024-25</option>
            </select>
          </div>
          <button className="w-full sm:w-auto bg-indigo-600 text-white px-8 py-2.5 rounded-lg hover:bg-indigo-700 active:scale-95 text-sm font-bold transition-all">
            Search
          </button>
        </div>

        {/* ── WORK HISTORY TABLE ── */}
        {/* MOBILE: empty state card */}
        <div className="block md:hidden">
          <h3 className="font-bold text-sm text-gray-700 uppercase tracking-wide border-b pb-2 mb-3">
            कामाचा इतिहास
          </h3>
          <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <AlertCircle size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">कामे उपलब्ध नाहीत</p>
          </div>
        </div>

        {/* DESKTOP: full 10-col table */}
        <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-300">
          <table className="w-full min-w-[960px] border-collapse text-sm">
            <thead>
              <tr className="bg-[#22165f] text-white">
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">Sr.No.</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">कामाचे नाव</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">लॉटरी क्रमांक</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">कामाची किंमत</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">विभागाचे नाव</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">काम पूर्ण करण्याची मुदत</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">काम पूर्ण केल्याचा दिनांक</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">स्थिती</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">शेरा</th>
                <th className="border border-[#3d2e8c] p-2.5 font-bold whitespace-nowrap">लेखाधिकारी स्वाक्षरी</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  colSpan="10"
                  className="border border-gray-200 text-center p-6 text-gray-400"
                >
                  कामे उपलब्ध नाहीत
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default SubheDashboard1;